import { RefObject, useEffect, useState } from "react";

type ClientRect = Omit<DOMRect, "x" | "y" | "toJSON">;

const EMPTY_RECT: ClientRect = {
  bottom: 0,
  height: 0,
  left: 0,
  right: 0,
  top: 0,
  width: 0,
};

export function useClientRect(
  elementRef: RefObject<HTMLDivElement>
): ClientRect {
  const [clientRect, setClientRect] = useState<ClientRect>(EMPTY_RECT);

  useEffect(() => {
    const element = elementRef.current;

    if (!element) {
      return;
    }

    const measure = () => {
      const { bottom, height, left, right, top, width } =
        element.getBoundingClientRect();

      setClientRect((previous) =>
        previous.height === height &&
        previous.width === width &&
        previous.top === top &&
        previous.left === left
          ? previous
          : { bottom, height, left, right, top, width }
      );
    };

    measure();
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(measure);
    observer?.observe(element);
    window.addEventListener("resize", measure, { passive: true });

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [elementRef]);

  return clientRect;
}
